import {
  type DrugCosts,
  PlanType,
  type UseTranslateTypeStrict,
} from "../../../@types";
import type { AppState } from "../../../@types/context";
import { isOverThirtyDaysSinceLogin } from "../../../helpers/beneficiaryInfoHelpers";
import { isValidPlanLongId } from "../../../helpers/planHelpers";
import { hasMailOrderPharmacy } from "../../store/appStoreHelpers";
import { NoSavedDrugsLink } from "./components/noSavedDrugsLinks";
import { NoSavedPharmaciesLink } from "./components/noSavedPharmaciesLinks";
import { OneSavedPharmacyLink } from "./components/oneSavedPharmacyLinks";
import { OneSavedPharmacyStaleAccountLink } from "./components/oneSavedPharmacyStaleAccountLinks";
import { alertMessageHierarchy } from "./constants";
import {
  AlertMessageCondition,
  AlertMessageHierarchyKeys,
  type AlertMessageType,
  type FormattedAlertMessage,
  type MessagesState,
  type AlertMessageContext,
} from "./types";

export const planComparePlanAlertMessageTestId =
  "alert-message-plan-compare-plan";

export function flattenMessagingState(
  state: Record<AlertMessageCondition, AlertMessageType>
): AlertMessageType[] {
  const result: AlertMessageType[] = [];
  for (const [key, value] of Object.entries(state)) {
    // `AlertMessageCondition` is a numeric enum, but `Object.entries` converts `key` to `string`
    result[Number(key)] = value;
  }
  return result;
}

const formatMessage = ({
  selectedMessage,
  t,
  alertMessageContext = {},
}: {
  selectedMessage: AlertMessageType;
  t: UseTranslateTypeStrict;
  alertMessageContext: AlertMessageContext;
}): FormattedAlertMessage => ({
  ...selectedMessage,
  title: selectedMessage.title
    ? selectedMessage.title(t, alertMessageContext)
    : undefined,
  action: selectedMessage.action
    ? selectedMessage.action(t, alertMessageContext)
    : undefined,
  body: selectedMessage.body(t, alertMessageContext),
  icon: selectedMessage.icon,
});

export function getMessageToShow({
  availableMessages,
  messageOrderHierarchy,
  t,
  alertMessageContext = {},
}: {
  availableMessages: AlertMessageType[];
  messageOrderHierarchy: Partial<AlertMessageCondition>[];
  t: UseTranslateTypeStrict;
  alertMessageContext: AlertMessageContext;
}): FormattedAlertMessage | undefined {
  const { planType, planId, planHasPreferredPharmacies } = alertMessageContext;
  let result: FormattedAlertMessage | undefined = undefined;
  for (const messageKey of messageOrderHierarchy) {
    const selectedMessage = availableMessages[messageKey];
    if (selectedMessage.active) {
      if (
        selectedMessage?.key ===
          AlertMessageCondition.NoPreferredSavedPharmacies &&
        planId &&
        !planHasPreferredPharmacies
      ) {
        // suppress preferred pharmacy alert if the plan does not offer any preferred pharmacies
        continue;
      } else if (
        planType === PlanType.MA &&
        selectedMessage?.key !== AlertMessageCondition.NoSavedDrugs
      ) {
        continue;
      } else {
        result = formatMessage({ selectedMessage, t, alertMessageContext });
        break;
      }
    }
  }
  return result;
}

/**
 * Overrides default LCP Alert Messaging by examining DrugCosts to derive Network
 * Status messages. It is used for Plan Compare (multiple plans on a page)
 *
 * - If no pharmacy is in-network, returns a message prompting to find an in-network
 * pharmacy
 * - If any pharmacy is in-network but there are no preferred pharmacies, returns
 * a message prompting to find a preferred pharmacy
 * - If any other type of message of higher priority is set to `active`, returns `undefined`
 *
 * This helper does not rely on the `active` property of network-status messages,
 * as it's passing in DrugCosts instead of relying on values set in AppState,
 * based on a plan also in AppState.
 *
 * Other types of messages with higher priority and `active` status will take
 * precedence over network-status messages.
 */
export function getNetworkStatusMessageForPlan({
  availableMessages,
  costs,
  hasMailOrderPharmacies,
  t,
  alertMessageContext,
}: {
  availableMessages: AlertMessageType[];
  costs: DrugCosts[];
  hasMailOrderPharmacies: boolean;
  t: UseTranslateTypeStrict;
  alertMessageContext: AlertMessageContext;
}): FormattedAlertMessage | undefined {
  const { planType, planHasPreferredPharmacies } = alertMessageContext;
  if (costs.length === 0 || planType === PlanType.MA) {
    return;
  }
  let result: FormattedAlertMessage | undefined = undefined;
  let costsToUse = [...costs];
  if (!hasMailOrderPharmacies) {
    costsToUse = [...costs].filter(cost => !cost.mail_order);
  }
  // Concat all messages for Plan Compare, because general alert messages set
  // to `active` will likely get priority over network status messages, if they
  // have a higher priority in the hierarchy (as of the time this was written,
  // they all do)
  const messageOrderHierarchy =
    alertMessageHierarchy[
      AlertMessageHierarchyKeys.PLAN_COMPARISON_NETWORK_STATUS
    ];
  const networkStatuses = costsToUse.reduce(
    (acc, curr) => ({
      in_network: acc.in_network ? acc.in_network : curr.in_network,
      preferred: acc.preferred ? acc.preferred : curr.preferred,
    }),
    { in_network: false, preferred: false }
  );
  for (const messageKey of messageOrderHierarchy) {
    const isNotNetworkStatusMessage =
      messageKey !== AlertMessageCondition.NoInNetworkSavedPharmacies &&
      messageKey !== AlertMessageCondition.NoPreferredSavedPharmacies;
    const hasPriorityOverNetworkMessages = // lower numbers are higher priority
      messageKey < AlertMessageCondition.NoInNetworkSavedPharmacies &&
      messageKey < AlertMessageCondition.NoPreferredSavedPharmacies;
    const selectedMessage = availableMessages[messageKey];
    // If one of the messages unrelated to network status is active, end the loop
    // and return undefined
    if (
      isNotNetworkStatusMessage &&
      hasPriorityOverNetworkMessages &&
      selectedMessage.active
    ) {
      result = undefined;
      break;
    }
    // Manual logic for setting network-status messages based on values taken
    // from drug costs for a specific plan being compared
    if (
      (!networkStatuses.in_network &&
        messageKey === AlertMessageCondition.NoInNetworkSavedPharmacies) ||
      (networkStatuses.in_network &&
        !networkStatuses.preferred &&
        planHasPreferredPharmacies !== false &&
        messageKey === AlertMessageCondition.NoPreferredSavedPharmacies)
    ) {
      result = formatMessage({ selectedMessage, t, alertMessageContext });
      continue;
    }
  }
  return result;
}

export function setMessageStatuses({
  messageState,
  appState,
}: {
  messageState: MessagesState;
  appState: AppState;
}): MessagesState {
  const { messages } = messageState;
  const result = { ...messages }; // shallow clone, don't directly mutate state
  const beneHasMailOrderPharmacy = hasMailOrderPharmacy(appState);
  const mailOrderCount = beneHasMailOrderPharmacy ? 1 : 0;
  const {
    mailOrderNetworkStatus = { preferred: false, inNetwork: false },
    prescriptions,
    pharmacies,
    beneficiary,
    routeParams,
  } = appState;
  const totalPharmacyCount = pharmacies.length + mailOrderCount;
  const drugsCount = prescriptions.length;
  const isAccountStale = beneficiary
    ? isOverThirtyDaysSinceLogin(beneficiary)
    : false;
  const hasInNetworkPharmacy =
    pharmacies.some(pharmacy => pharmacy.in_network) ||
    (beneHasMailOrderPharmacy && mailOrderNetworkStatus.inNetwork);
  const hasPreferredPharmacy =
    pharmacies.some(pharmacy => pharmacy.preferred) ||
    (beneHasMailOrderPharmacy && mailOrderNetworkStatus.preferred);

  // Conditions in which network pharmacy status alerts should be suppressed
  let suppressPharmacyNetworkStatusMessages = false;
  const hasRoutePlanId = isValidPlanLongId(routeParams?.id || "");
  if (beneficiary && !beneficiary.coverage_current[0]) {
    suppressPharmacyNetworkStatusMessages = !hasRoutePlanId;
  }

  for (const [key, value] of Object.entries(messages)) {
    switch (Number(key)) {
      case AlertMessageCondition.NoSavedPharmacies: {
        result[AlertMessageCondition.NoSavedPharmacies] = {
          ...value,
          active: totalPharmacyCount < 1,
          // action is overwritten here to provide state context to link
          action: (t, alertMessageContext) => (
            <NoSavedPharmaciesLink
              alertMessageContext={alertMessageContext}
              className="ds-c-button ds-c-button--solid ds-c-button--alternate"
              treatAsButton
            >
              {t("lcp.messages.add_pharmacies.button")}
            </NoSavedPharmaciesLink>
          ),
        };
        break;
      }
      case AlertMessageCondition.NoSavedDrugs: {
        result[AlertMessageCondition.NoSavedDrugs] = {
          ...value,
          active: drugsCount < 1,
          // action is overwritten here to provide state context to link
          action: (t, alertMessageContext) => (
            <NoSavedDrugsLink
              alertMessageContext={alertMessageContext}
              className="ds-c-button ds-c-button--solid ds-c-button--alternate"
              treatAsButton
            >
              {t("lcp.messages.add_drugs.button")}
            </NoSavedDrugsLink>
          ),
        };
        break;
      }
      case AlertMessageCondition.OneSavedPharmacyStaleAccount: {
        result[AlertMessageCondition.OneSavedPharmacyStaleAccount] = {
          ...value,
          active: totalPharmacyCount === 1 && isAccountStale,
          action: (t, alertMessageContext) => (
            <OneSavedPharmacyStaleAccountLink
              alertMessageContext={alertMessageContext}
              className="ds-c-button ds-c-button--solid ds-c-button--alternate"
              treatAsButton
            >
              {t("lcp.messages.add_more_pharmacies_stale.button")}
            </OneSavedPharmacyStaleAccountLink>
          ),
        };
        break;
      }
      case AlertMessageCondition.OneSavedPharmacy: {
        result[AlertMessageCondition.OneSavedPharmacy] = {
          ...value,
          active: totalPharmacyCount === 1,
          // action is overwritten here to provide state context to link
          action: (t, alertMessageContext) => (
            <OneSavedPharmacyLink
              className="ds-c-button ds-c-button--solid ds-c-button--alternate"
              alertMessageContext={alertMessageContext}
              treatAsButton
            >
              {t("lcp.messages.add_more_pharmacies_standard.button")}
            </OneSavedPharmacyLink>
          ),
        };
        break;
      }
      case AlertMessageCondition.StaleAccountPharmacies: {
        result[AlertMessageCondition.StaleAccountPharmacies] = {
          ...value,
          active: isAccountStale,
        };
        break;
      }
      case AlertMessageCondition.StaleAccountDrugs: {
        result[AlertMessageCondition.StaleAccountDrugs] = {
          ...value,
          active: isAccountStale,
        };
        break;
      }
      case AlertMessageCondition.NoInNetworkSavedPharmacies: {
        result[AlertMessageCondition.NoInNetworkSavedPharmacies] = {
          ...value,
          active:
            !hasInNetworkPharmacy && !suppressPharmacyNetworkStatusMessages,
        };
        break;
      }
      case AlertMessageCondition.NoPreferredSavedPharmacies: {
        result[AlertMessageCondition.NoPreferredSavedPharmacies] = {
          ...value,
          active:
            !hasPreferredPharmacy && !suppressPharmacyNetworkStatusMessages,
        };
        break;
      }
    }
  }
  return { messages: result };
}
